import { useState } from "react";
import { MediaFileCard } from "#app/components/media/media-file-card";
import {
  MEDIA_TILE_SIZE,
  ResolvedMediaTile,
} from "#app/components/media/media-tiles";
import { MediaVideoDialog } from "#app/components/media/media-video-dialog";
import { useMediaActions } from "#app/lib/hooks/use-note-media";
import { mediaKind } from "#app/lib/media-kind";
import type { NoteMedia as NoteMediaItem } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

export type { NoteMediaItem };

/**
 * A single posted-note attachment, rendered by media class:
 * - image  → thumbnail tile; click opens the image lightbox (via onImageClick).
 * - pdf    → page-1 thumbnail tile; click opens it inline in a new tab.
 * - video  → poster tile with a play badge; click opens the inline player.
 * - file   → file card (icon + name); click downloads/opens it.
 */
export function MediaAttachmentTile({
  item,
  size = MEDIA_TILE_SIZE,
  onImageClick,
}: Omittable<{
  item: NoteMediaItem;
  size?: number;
  /** Called when an IMAGE tile is clicked (open lightbox at this image). */
  onImageClick?: () => void;
}>) {
  const kind = mediaKind(item.contentType);
  const { openMedia } = useMediaActions();
  const [videoOpen, setVideoOpen] = useState(false);

  if (kind === "file") {
    return (
      <MediaFileCard
        contentType={item.contentType}
        fileName={item.fileName}
        size={size}
        onClick={() => openMedia(item.id)}
      />
    );
  }

  const onClick =
    kind === "image"
      ? onImageClick
      : kind === "pdf"
        ? () => openMedia(item.id)
        : () => setVideoOpen(true);

  return (
    <>
      {/* ResolvedMediaTile renders the thumbnail and, for video, the play
          badge — kind awareness lives there. */}
      <ResolvedMediaTile
        photoId={item.id}
        contentType={item.contentType}
        size={size}
        onClick={onClick}
      />
      {kind === "video" ? (
        <MediaVideoDialog
          photoId={item.id}
          fileName={item.fileName}
          open={videoOpen}
          onOpenChange={setVideoOpen}
        />
      ) : null}
    </>
  );
}
